/**
 * The three ways to add documents to a collection — Azure Blob, Upload, Veeva
 * Vault — as tabs. Used by the create wizard and by the detail page.
 *
 * Adding is metadata only; nothing is read or indexed until the next build.
 */

import { Tabs } from "@mantine/core";
import { IconBrandAzure, IconFileCertificate, IconUpload } from "@tabler/icons-react";
import type { AddDocumentsOut } from "@/shared/api/indexes";
import { toastSuccess } from "@/shared/ui/toast";
import { BlobSourceTab } from "./BlobSourceTab";
import { UploadSourceTab } from "./UploadSourceTab";
import { VeevaSourceTab } from "./VeevaSourceTab";

interface Props {
  indexId: string;
  onAdded: (result: AddDocumentsOut) => void;
}

export function AddDocumentsPanel({ indexId, onAdded }: Props) {
  const handle = (result: AddDocumentsOut) => {
    const n = result.added.length;
    const skipped = result.added.filter((d) => d.status === "skipped").length;
    const parts = [`${n} document${n === 1 ? "" : "s"} added`];
    if (skipped > 0) parts.push(`${skipped} will be skipped (unsupported type)`);
    if (result.skipped_duplicates > 0) parts.push(`${result.skipped_duplicates} already present`);
    toastSuccess("Documents added", `${parts.join(" · ")}.`);
    onAdded(result);
  };

  return (
    <Tabs defaultValue="blob" keepMounted={false}>
      <Tabs.List mb="md">
        <Tabs.Tab value="blob" leftSection={<IconBrandAzure size={16} />}>
          Azure Blob
        </Tabs.Tab>
        <Tabs.Tab value="upload" leftSection={<IconUpload size={16} />}>
          Upload
        </Tabs.Tab>
        <Tabs.Tab value="veeva" leftSection={<IconFileCertificate size={16} />}>
          Veeva Vault
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="blob">
        <BlobSourceTab indexId={indexId} onAdded={handle} />
      </Tabs.Panel>
      <Tabs.Panel value="upload">
        <UploadSourceTab indexId={indexId} onAdded={handle} />
      </Tabs.Panel>
      <Tabs.Panel value="veeva">
        <VeevaSourceTab indexId={indexId} onAdded={handle} />
      </Tabs.Panel>
    </Tabs>
  );
}
